import {
  fetchPostIwocapayPaymentLater,
  fetchPostIwocapayPaymentNow,
} from '@iwoca/lapi-client/edge';

import styles from './GenericPayLinkLanding.module.css';
import { RequestedDuration } from '../../../api/lending/edge';
import {
  SELLER_GET_ERROR,
  useGetIwocapayBuyerPayLinkPackage,
  useGetSellerByHandle,
} from '../../../api/lending/lapiHooks';
import { LoadingScreen } from '../../../components/LoadingScreen/LoadingScreen';
import { ReactComponent as IwocaPayIcon } from '../../../components/svg/IwocaPayIcon.svg';
import { useQueryParam } from '../../../hooks/useQueryParam';
import { GenericError } from '../../../Pages/FailurePages/GenericError';
import { createBuyerAccount } from '../../../Pages/stateApi';
import { FormalOffer } from '../../../Seller/PayLinks/utils/Paylinks.types';
import {
  trackSelectedCheckoutOption,
  trackSelectedOffer,
} from '../../../utils/tracking';
import {
  TEnabledOptions,
  useEnabledOptions,
} from '../../hooks/useEnabledTerms';
import { useTrackLandingPageView } from '../../hooks/useTrackLandingPageView';
import { SIGNUP_BASE_PATH } from '../../Signup/routes';
import { createState } from '../../utils/customerState';
import { buildQueryString } from '../../utils/queryParams';
import { HelpTextAccordion } from '../HelpTextAccordion/HelpTextAccordion';
import { useGetValidIwocapayFormalOffers } from '../hooks/useGetValidIwocapayFormalOffers';
import landingStyles from '../PayLinkLanding.module.css';
import { PaymentDetails } from '../PaymentDetails/PaymentDetails';
import { FormValues } from '../utils/PayLinkLanding.types';
import {
  getBuyerInterest,
  getPricingPromotions,
  getRelevantFormalOfferId,
  shouldRedirectToCheckout,
} from '../utils/productPricingHelpers';

export const GenericPayLinkLanding = ({
  sellerHandle,
  payLinkPackageId,
}: {
  sellerHandle: string;
  payLinkPackageId?: string;
}) => {
  const { sellerError, seller, loadingSeller } =
    useGetSellerByHandle(sellerHandle);

  const amount = useQueryParam('amount');
  const reference = useQueryParam('reference');

  const { formalOffers, loadingFormalOffers } =
    useGetValidIwocapayFormalOffers();

  const { payLinkPackage, loadingPayLinkPackage } =
    usePayLinkPackage(payLinkPackageId);
  const { enabledOptions, loadingOptions } = useEnabledOptions({
    pricing: payLinkPackage?.product_pricing || seller?.product_pricing,
  });

  const queryParamAmount = amount
    ? parseFloat(amount.replace(',', '')).toFixed(2)
    : undefined;

  const initialReference = payLinkPackage?.reference || reference || '';
  const initialAmount = payLinkPackage?.amount || queryParamAmount || '';

  useTrackLandingPageView({
    payLinkType: 'generic',
    visibleOptions: enabledOptions,
    productPricing: payLinkPackage
      ? payLinkPackage.product_pricing
      : seller?.product_pricing,
    sellerId: seller?.seller_id,
    loadingApis: loadingPayLinkPackage || loadingSeller,
  });

  if (sellerError) {
    return <GenericError errorText={sellerError as string} />;
  }

  if (!seller || loadingSeller || loadingOptions || loadingFormalOffers) {
    return <LoadingScreen />;
  }

  if (payLinkPackage?.deleted_at) {
    return <GenericError errorText={SELLER_GET_ERROR} />;
  }

  const productPricing = payLinkPackage
    ? payLinkPackage.product_pricing
    : seller.product_pricing;

  const buyerInterest = getBuyerInterest({
    productPricing,
    formalOffers: formalOffers,
  });

  const pricingPromotions = getPricingPromotions({
    productPricing,
  });

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Your payment to {seller.trading_name}</h1>
          <p className={styles.poweredBy}>
            Powered by&nbsp;
            <IwocaPayIcon className={landingStyles.iwocaPayLogo} />
          </p>
        </div>

        <div className={styles.contentContainer}>
          <div className={styles.paymentDetailsColumn}>
            <PaymentDetails
              sellerName={seller.trading_name}
              pricingPromotions={pricingPromotions}
              buyerInterest={buyerInterest}
              onSubmit={onSubmit({
                sellerHandle,
                payLinkPackageId,
                formalOffers,
                enabledOptions,
              })}
              initialValues={{
                amount: initialAmount.toString(),
                reference: initialReference,
              }}
              enabledOptions={enabledOptions}
              payLinkType="generic"
            />
            <p className={styles.privacyPolicy}>
              Find out how we use your data in our&nbsp;
              <a
                href="/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </p>
          </div>

          <div className={styles.helpAccordionColumn}>
            <HelpTextAccordion
              className={styles.helpAccordion}
              enabledOptions={enabledOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const onSubmit =
  ({
    sellerHandle,
    payLinkPackageId,
    formalOffers,
    enabledOptions,
  }: {
    sellerHandle: string;
    payLinkPackageId?: string;
    formalOffers?: FormalOffer[];
    enabledOptions: TEnabledOptions;
  }) =>
  async ({ values, stateKey }: { values: FormValues; stateKey?: string }) => {
    const { paymentOption, reference } = values;
    const amount = parseFloat(values.amount);

    if (paymentOption === 'payNow') {
      const payNowResponse = await fetchPostIwocapayPaymentNow({
        sellerHandle,
        body: {
          data: {
            amount,
            reference,
            pay_link_package_id: payLinkPackageId,
          },
        },
      });

      window.location.href = payNowResponse.data!.payment_provider_auth_uri;
    } else {
      const selectedOption = (
        values.paymentOption === 'payLater3' ? 'THREE_MONTHS' : 'TWELVE_MONTHS'
      ) as RequestedDuration;
      const payLaterRequestBody = {
        data: {
          amount,
          reference,
          seller_handle: sellerHandle,
          pay_link_package_id: payLinkPackageId,
          requested_duration: selectedOption,
        },
      };

      if (stateKey) {
        const payLinkResponse = await fetchPostIwocapayPaymentLater({
          stateKey,
          body: payLaterRequestBody,
        });
        const payLink = await payLinkResponse.json();

        trackSelectedCheckoutOption({
          payLinkId: payLink.data.id as string,
          visibleOptions: enabledOptions,
          selectedOption: selectedOption,
        });

        if (
          shouldRedirectToCheckout({
            paymentOption,
            payLinkAmount: amount,
            formalOffers,
          })
        ) {
          const offerId = getRelevantFormalOfferId({
            paymentOption,
            formalOffers,
          });
          if (offerId) {
            trackSelectedOffer({
              payLinkId: payLink.data.id as string,
              offerIds: formalOffers!.map((offer) => offer.offer_id!),
              offerId: offerId,
            });

            window.location.href = `/pay/checkout/offer/${buildQueryString({
              payLinkId: payLink.data.id,
              ...(offerId && { offerId: offerId }),
            })}`;
            return;
          }
        }
        window.location.href = `/account/iwocapay/${buildQueryString({
          source: 'iwocapay-pay-link',
        })}`;
      } else {
        const { emailAddress, marketingOptIn } = values;
        const customerState = await createBuyerAccount(
          createState(emailAddress, marketingOptIn),
        );

        const payLinkResponse = await fetchPostIwocapayPaymentLater({
          stateKey: customerState.data!.state_key,
          body: payLaterRequestBody,
        });
        const payLink = await payLinkResponse.json();

        trackSelectedCheckoutOption({
          payLinkId: payLink.data.id as string,
          visibleOptions: enabledOptions,
          selectedOption: selectedOption,
        });

        window.location.href = SIGNUP_BASE_PATH + buildQueryString();
      }
    }
  };

function usePayLinkPackage(payLinkPackageId?: string) {
  const { iwocapayBuyerPayLinkPackage: payLinkPackage, loading } =
    useGetIwocapayBuyerPayLinkPackage({
      payLinkPackageId,
      enabled: !!payLinkPackageId,
    });

  return {
    payLinkPackage,
    loadingPayLinkPackage: loading,
  };
}
